import { useQueryClient } from '@tanstack/react-query';
import {
  broadcastAuthLogout,
  clearAuthLoggingOut,
  markAuthLoggingOut,
} from '../utils/auth-sync';
import { getHomePageUrl } from '../utils/auth-redirect';
import type { logout as logoutAction } from '../stores/slices/authSlice';

/**
 * useLogout Hook 配置
 */
export interface UseLogoutConfig {
  /** tRPC 实例 */
  trpc: any;
  
  /** Redux dispatch 函数 */
  dispatch: any;
  
  /** logout action creator */
  logout: typeof logoutAction;
  
  /**
   * 退出成功后的回调（可选）
   */
  onSuccess?: () => void;
  
  /**
   * 退出失败后的回调（可选）
   */
  onError?: (error: any) => void;
  
  /**
   * 是否在退出后跳转到 home 主页（默认 true）
   */
  redirectToHome?: boolean;
}

/**
 * useLogout 返回值
 */
export interface UseLogoutResult {
  /** 退出登录函数 */
  logout: () => void;
  
  /** 是否正在退出 */
  isLoggingOut: boolean;
}

/**
 * 统一退出登录 Hook
 * 
 * 职责：
 * 1. 调用后端 logout API 清除 Cookie
 * 2. 清除 Redux 状态
 * 3. 跳转到 home 主页
 * 
 * 优点：
 * - 统一的退出逻辑
 * - 避免在各个模块中重复代码
 * - 易于维护和升级
 * 
 * @example
 * ```tsx
 * import { useLogout, logout as logoutAction } from '@hs/shared';
 * 
 * function AppNavbar() {
 *   const dispatch = useAppDispatch();
 *   const { logout, isLoggingOut } = useLogout({
 *     trpc,
 *     dispatch,
 *     logout: logoutAction,
 *   });
 * 
 *   return (
 *     <Button onClick={logout} loading={isLoggingOut}>
 *       退出登录
 *     </Button>
 *   );
 * }
 * ```
 */
export function useLogout(config: UseLogoutConfig): UseLogoutResult {
  const {
    trpc,
    dispatch,
    logout: logoutActionCreator,
    onSuccess,
    onError,
    redirectToHome = true,
  } = config;
  
  const queryClient = useQueryClient();

  const logoutMutation = trpc.auth.logout.useMutation();

  const navigateAfterLogout = () => {
    if (redirectToHome && typeof window !== 'undefined') {
      const homePageUrl = getHomePageUrl();
      console.log('🔄 [useLogout] 跳转到 home 主页:', homePageUrl);
      clearAuthLoggingOut();
      // 整页跳转，勿先 dispatch logout，避免 RouteGuard 抢先 redirectToHomeLogin
      window.location.replace(homePageUrl);
      return true;
    }
    return false;
  };

  const finishLocalLogoutWithoutRedirect = () => {
    broadcastAuthLogout();
    queryClient.clear();
    dispatch(logoutActionCreator());
  };

  return {
    logout: () => {
      void (async () => {
        markAuthLoggingOut();
        try {
          await logoutMutation.mutateAsync();
          console.log('✅ [useLogout] 退出登录成功，清除 Cookie');
          queryClient.clear();
          broadcastAuthLogout();
          if (navigateAfterLogout()) return;
          dispatch(logoutActionCreator());
          onSuccess?.();
          clearAuthLoggingOut();
          return;
        } catch (error: unknown) {
          console.error('❌ [useLogout] 退出登录失败:', error);
          queryClient.clear();
          broadcastAuthLogout();
          if (navigateAfterLogout()) return;
          finishLocalLogoutWithoutRedirect();
          onError?.(error);
          clearAuthLoggingOut();
        }
      })();
    },
    isLoggingOut: logoutMutation.isPending,
  };
}

